import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaExclamation } from 'react-icons/fa';
import { MdOutlineArrowBack } from 'react-icons/md';
import {
    RiFlagFill,
    RiMoreFill,
    RiCloseFill
} from 'react-icons/ri';
import moment from 'moment';
import CoursePlaceholderImage from 'assets/vectors/CoursePlaceholder.svg';
import axios from 'config/axios';
import Helmet from 'components/Helmet';
import Button from 'components/Button';
import ButtonLink from 'components/ButtonLink';
import Loader from 'components/Loader';
import ScoreWithScale from 'components/ScoreWithScale';
import RadarChart from 'components/RadarChart';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { updateCart } from 'store/cart';
import { setError, setAlert } from 'store/error';
import './index.css';

const timePerQuestionRW = 64 * 60 / 54;
const timePerQuestionMath = 70 * 60 / 44;
const MULTIPLE_CHOICES = ['A', 'B', 'C', 'D', 'E'];
const today = new Date();

function TestReport() {
    const [testData, setTestData] = useState(null);
    const [popup, setPopup] = useState(null);
    const [moduleIndex, setModuleIndex] = useState(0);

    const { testId } = useParams();
    // const { bundles: cartBundles, courses: cartCourses } = useAppSelector(state => state.cart);
    // const { courses: classroomCourses } = useAppSelector(state => state.classroom);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const ref = useOutsideClickHandler(() => setPopup(null));

    const refreshCart = () => {
        axios.get('payments/cart/')
            .then(res => {
                // console.log(res.data);
                dispatch(updateCart({
                    bundles: res.data?.cartBundles,
                    courses: res.data?.cartCourses,
                    tests: res.data?.cartTests
                }));
            });
    }

    const formatTime = (time: number) => {
        const duration = moment.duration(time, 'seconds');

        const hours = duration.hours();
        const minutes = (hours && duration.minutes() < 10) ? `0${duration.minutes()}` : duration.minutes();
        const seconds = duration.seconds() < 10 ? `0${duration.seconds()}` : duration.seconds();

        return hours ? `${hours}:${minutes}:${seconds}` : `${minutes}:${seconds}`;
    }

    const addToCart = (courseUrl: string) => {
        axios.post(`payments/cart/add/course/${courseUrl}/`)
            .then(res => {
                // console.log(res.data);
                refreshCart();
            })
            .catch(error => {
                // console.log(error);
                dispatch(setAlert(error));
            });
    }

    const addBundleToCart = (bundleId: string) => {
        axios.post(`payments/cart/add/bundle/${bundleId}/`)
            .then(res => {
                // console.log(res.data);
                refreshCart();
            })
            .catch(error => {
                // console.log(error);
                dispatch(setAlert(error));
            });
    }

    const powerSet = (array: any[]) => {
        return new Array(1 << array.length).fill(null).map((_e1, i) => (
            array.filter((_e2, j) => i & 1 << j)
        )).sort((a, b) => b.length - a.length);
    }

    useEffect(() => {
        axios.get(`tests/result/${testId}/`)
            .then(res => {
                setTestData(res.data);
            })
            .catch(error => {
                // console.log(error);
                dispatch(setError(error));
            });
    }, []);

    const startCourse = (courseUrl: string) => {
        axios.post(`courses/classroom/${courseUrl}/start/`)
            .then(res => {
                navigate(`/courses/${courseUrl}`);
            })
            .catch(error => {
                // console.log(error);
                if (error.response) {
                    dispatch(setError({ message: error.response.data.message }));
                }
            });
    }

    if (!testData) return <Loader />;

    const lecturesPopup = popup ? (
        <div className='testReport__lecturesPopup' ref={ref}>
            <div className='testReport__lecturesHeader'>
                <h2>{testData.sections[popup[0]].subject}</h2>
                {popup[1] === 'satisfactory'
                    ?   <h3>
                            Satisfactory
                            <RiFlagFill className='testReport__satisfactory' />
                        </h3>
                    :   popup[1] === 'advised'
                    ?   <h3>
                            Advised
                            <RiMoreFill className='testReport__advisedToReview' />
                        </h3>
                    :   popup[1] === 'critical'
                    ?   <h3>
                            Critical
                            <FaExclamation className='testReport__critical' />
                        </h3>
                    :   <></>
                }
                <RiCloseFill
                    className='testReport__lecturesPopupClose'
                    onClick={() => setPopup(null)}
                />
            </div>
            <div className='testReport__lecturesList'>
                {testData.sections[popup[0]].leafCategories.filter(category => category.level === popup[1]).map(category => (
                    <p className='testReport__lecture' key={category.name}>
                        {category.name}
                    </p>
                ))}
                {/* {testData.sections[popup[0]].course.lectures[popup[1]].map((lecture, l) => (
                    <p className='testReport__lecture' key={lecture.name + l}>
                        {lecture.name} / {Math.round(moment.duration(lecture.duration).asMinutes())} min
                    </p>
                ))} */}
            </div>
        </div>
    ) : <></>;

    return (
        <div className='layout'>
            <Helmet
                title={`${testData.testName} Report - The Digital SAT Crash Course`}
            />
            <div className='testReport'>
                <button
                    className='testReport__back'
                    onClick={() => navigate('/courses')}
                >
                    <MdOutlineArrowBack className='testReport__backIcon' />
                    Back to My Courses
                </button>

                <h1 className='testReport__header'>
                    Your predicted SAT score
                </h1>
                <div className='testReport__section multiple_info'>
                    <ScoreWithScale
                        className='testReport__totalScore'
                        score={parseInt(testData.score)}
                        scoreMin={parseInt(testData.minScore)}
                        scoreMax={parseInt(testData.maxScore)}
                        fontSize={64}
                    />
                    <table className='testReport__infoTable'>
                        <tbody>
                            <tr>
                                <td>Student</td>
                                <th>{testData.student}</th>
                            </tr>
                            <tr>
                                <td>Test Date</td>
                                <th>{new Date(testData.testDate).toLocaleDateString(undefined, {
                                    weekday: 'short',
                                    year: 'numeric', 
                                    month: 'short',
                                    day: 'numeric'
                                })}</th>
                            </tr>
                            <tr>
                                <td>Test Name</td>
                                <th>{testData.testName}</th>
                            </tr>
                        </tbody>
                    </table>
                    <div className='testReport__buttons'>
                        <ButtonLink
                                className='testReport__button'
                                to={`/review/digital-sat/${testId}`}
                                secondary
                            >
                                REVIEW QUESTIONS
                        </ButtonLink>
                        {testData.testType=='Digital SAT Level Test'&& (
                            testData.endDate && new Date(testData.endDate) < today
                            ?   (testData.syllabusUrl
                                ?   <a
                                        className='testReport__notes'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        href={testData.syllabusUrl}
                                    >
                                        VIEW COURSE NOTES
                                    </a>
                                :   <></>
                                )
                            :   testData.startDate && new Date(testData.startDate) <= today
                            ?   <ButtonLink
                                    className='testReport__button'
                                    to={`/courses/${testData.urlSlug}`}
                                >
                                    CONTINUE STUDYING
                                </ButtonLink>
                            :   testData.preTest && !testData.preTest.isTaken
                            ?   <></>
                            :    <Button
                                    className='testReport__button'
                                    onClick={() => startCourse(testData.urlSlug)}
                                >
                                    START STUDYING
                                </Button>
                            )
                        }
                    </div>
                </div>

                <h1 className='testReport__header'>
                    Evidence-based score breakdown by section
                </h1>
                {/* <h2 className='testReport__subjectName'>
                    {testData.sections[0].subject} 
                </h2> */}
                <div className='testReport__section multiple_info'>
                    {testData.sections.map(section => (
                        <div className='testReport__scoreBreakdown' key={section.name}>
                            <h2 className='testReport__subjectName'>
                                {section.subject} Section Overview
                            </h2>
                            <div className='testReport__scoreBreakdownInfo'>
                                {/* <ScoreWithScale
                                    score={parseInt(section.score)}
                                    scoreMin={parseInt(section.minScore)}
                                    scoreMax={parseInt(section.maxScore)}
                                /> */}
                                <div className='testReport__chartWrapper'>
                                    <RadarChart
                                        labels={section.rootCategories.map(category => category.name)}
                                        data={section.rootCategories.map(category => (category.correct/category.total*100).toFixed(1))}
                                        min={-25}
                                        stepSize={25}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                    {/* {testData.sections?.map(section => {
                        const weakCategories = section.leafCategories.filter(category => (
                            category.correct < category.total
                        )).sort((a, b) => (a.correct/a.total === b.correct/b.total
                            ? b.total - a.total
                            : a.correct/a.total - b.correct/b.total
                        ));
        
                        const topWeakCategories = weakCategories.length > 5 ? weakCategories.slice(0, 5) : weakCategories;
                        return (
                            <div className='testReport__scoreBreakdown' key={section.name}>
                                <h2 className='testReport__subjectName'>
                                    Your Weak Areas in {section.subject}
                                </h2>
                                <div className='testReport__scoreBreakdownInfo'>
                                    <div className='testReport__chartWrapper'>
                                        <RadarChart
                                            labels={topWeakCategories?.map(category => category.name)}
                                            data={topWeakCategories?.map(category => (category.correct/category.total*100).toFixed(1))}
                                            min={-25}
                                            stepSize={25}
                                        />
                                    </div>
                                </div>
                            </div>  
                        )
                    })} */}
                </div>

                {testData.sections.filter(section => section.course).length > 0 && <>
                    <h1 className='testReport__header'>
                        Courses breakdown based on assessment levels
                    </h1>
                    <div className='testReport__section'>
                        {testData.sections.filter(section => section.course).map(({ course, subject }, s) => (
                            <div className='testReport__card' key={subject}>
                                <h2 className='testReport__subjectName'>
                                    {subject}
                                </h2>
                                <div className='testReport__recommended'>
                                    <h3 className='testReport__recommendedLevel'>
                                        Satisfactory
                                        <RiFlagFill className='testReport__satisfactory' />
                                    </h3>
                                    <p className='testReport__recommendedCourses'>
                                        {course.lectures.satisfactory.length} {course.lectures.satisfactory.length > 1 ? 'lectures' : 'lecture'}
                                        &nbsp;/&nbsp;
                                        {Math.round(course.lectures.satisfactory.reduce((total, lecture) => (
                                            total + moment.duration(lecture.duration).asMinutes()), 0
                                        ))} min
                                    </p>
                                    <p className={course.lectures.satisfactory.length > 0
                                        ? 'testReport__recommendedViewMore'
                                        : 'testReport__recommendedViewMore--disabled'
                                    } onClick={() => setPopup([s, 'satisfactory'])}>
                                        View topics
                                    </p>
                                    {popup && popup[0] === s && popup[1] === 'satisfactory' && lecturesPopup}
                                </div>
                                <div className='testReport__recommended'>
                                    <h3 className='testReport__recommendedLevel'>
                                        Advised to review
                                        <RiMoreFill className='testReport__advisedToReview' />
                                    </h3>
                                    <p className='testReport__recommendedCourses'>
                                        {course.lectures.advised.length} {course.lectures.advised.length > 1 ? 'lectures' : 'lecture'}
                                        &nbsp;/&nbsp;
                                        {Math.round(course.lectures.advised.reduce((total, lecture) => (
                                            total + moment.duration(lecture.duration).asMinutes()), 0
                                        ))} min
                                    </p>
                                    <p className={course.lectures.advised.length > 0
                                        ? 'testReport__recommendedViewMore'
                                        : 'testReport__recommendedViewMore--disabled'
                                    } onClick={() => setPopup([s, 'advised'])}>
                                        View topics
                                    </p>
                                    {popup && popup[0] === s && popup[1] === 'advised' && lecturesPopup}
                                </div>
                                <div className='testReport__recommended'>
                                    <h3 className='testReport__recommendedLevel'>
                                        Critical
                                        <FaExclamation className='testReport__critical' />
                                    </h3>
                                    <p className='testReport__recommendedCourses'>
                                        {course.lectures.critical.length} {course.lectures.critical.length > 1 ? 'lectures' : 'lecture'}
                                        &nbsp;/&nbsp;
                                        {Math.round(course.lectures.critical.reduce((total, lecture) => (
                                            total + moment.duration(lecture.duration).asMinutes()), 0
                                        ))} min
                                    </p>
                                    <p className={course.lectures.critical.length > 0
                                        ? 'testReport__recommendedViewMore'
                                        : 'testReport__recommendedViewMore--disabled'
                                    } onClick={() => setPopup([s, 'critical'])}>
                                        View topics
                                    </p>
                                    {popup && popup[0] === s && popup[1] === 'critical' && lecturesPopup}
                                </div>
                            </div>
                        ))}
                    </div>
                </>}

                <h1 className='testReport__header'>
                    Test result detailed breakdown
                </h1>
                <div className='testReport__questions'>
                    {testData.testModules.length > 1 &&
                        <div className='testReport__moduleTabs'>
                            {testData.testModules.map((testModule, t) => (
                                <button
                                    className={moduleIndex === t
                                        ? 'testReport__moduleTab--active'
                                        : 'testReport__moduleTab'
                                    }
                                    onClick={() => setModuleIndex(t)}
                                    key={testModule.name}
                                >
                                    Module {testModule.moduleNumber} {testModule.moduleNumber === 2 && (testModule.hard ? '(Hard)' : '(Easy)')}
                                </button>
                            ))}
                            <div className={moduleIndex === 0
                                ? 'testReport__moduleTabIndicatorLeft'
                                : 'testReport__moduleTabIndicatorRight'
                            }></div>
                        </div>
                    }
                    <h2 className='testReport__questionsHeader'>
                        Questions Overview
                    </h2>
                    <div className='testReport__questionsOverview'>
                        <div className='testReport__questionsSummary'>
                            <div className='testReport__questionsSummaryItem'>
                                <p className='testReport__questionSummaryNumber'>
                                    {testData.testModules.filter(testModule => (
                                        testModule.moduleNumber === moduleIndex + 1
                                    )).reduce((total, testModule) => (
                                        total + testModule.answers.length
                                    ), 0)}
                                </p>
                                <p className='testReport__questionSummaryText'>
                                    Total Questions
                                </p>
                            </div>
                            <div className='testReport__questionsSummaryItem'>
                                <p className='testReport__questionSummaryNumber'>
                                    {testData.testModules.filter(testModule => (
                                        testModule.moduleNumber === moduleIndex + 1
                                    )).reduce((total, testModule) => (
                                        total + testModule.answers.filter(answer => answer.isCorrect).length
                                    ), 0)}
                                </p>
                                <p className='testReport__questionSummaryText'>
                                    Correct Answers
                                </p>
                            </div>
                            <div className='testReport__questionsSummaryItem'>
                                <p className='testReport__questionSummaryNumber'>
                                    {testData.testModules.filter(testModule => (
                                        testModule.moduleNumber === moduleIndex + 1
                                    )).reduce((total, testModule) => (
                                        total + testModule.answers.filter(answer => !answer.isCorrect).length
                                    ), 0)}
                                </p>
                                <p className='testReport__questionSummaryText'>
                                    Incorrect Answers
                                </p>
                            </div>
                        </div>
                        <table className='testReport__questionsTable'>
                            <tbody>
                                <tr>
                                    <th>Question</th>
                                    <th>Correct Answer</th>
                                    <th>Your Answer</th>
                                    <th>Category</th>
                                    <th>Time Spent</th>
                                </tr>
                                {testData.testModules[moduleIndex].answers.map(answer => (
                                    <tr key={testData.testModules[moduleIndex].name + answer.questionNumber}>
                                        <td>{answer.questionNumber}</td>
                                        <td>{answer.correctChoice !== null
                                            ?   MULTIPLE_CHOICES[answer.correctChoice]
                                            :   answer.correctAnswers.toString().replaceAll(',', ', ')
                                        }</td>
                                        <td>{answer.answer !== null
                                            ?   (answer.isCorrect
                                                    ? <p>{answer.answer}; Correct</p>
                                                    : <p className='testReport__questionIncorrect'>{answer.answer}; Incorrect</p>
                                                )
                                            :   answer.choice !== null
                                            ?   (answer.choice === answer.correctChoice
                                                    ? <p>{MULTIPLE_CHOICES[answer.choice]}; Correct</p>
                                                    : <p className='testReport__questionIncorrect'>{MULTIPLE_CHOICES[answer.choice]}; Incorrect</p>
                                                )
                                            :   <p className='testReport__questionOmitted'>Omitted</p>
                                        }</td>
                                        <td>{answer.category}</td>
                                        <td>
                                            <p className={answer.timeSpent > (testData.testModules[moduleIndex].subject === 'SAT Reading and Writing'
                                                        ? timePerQuestionRW * 1.5
                                                        : testData.testModules[moduleIndex].subject === 'SAT Math'
                                                        ? timePerQuestionMath * 1.5
                                                        : 60 * 1.5
                                                    )
                                                ?   'testReport__timeSpentBad'
                                                :   answer.timeSpent > (testData.testModules[moduleIndex].subject === 'SAT Reading and Writing'
                                                        ? timePerQuestionRW
                                                        : testData.testModules[moduleIndex].subject === 'SAT Math'
                                                        ? timePerQuestionMath
                                                        : 60
                                                    )
                                                ?   'testReport__timeSpentWarning'
                                                :   'testReport__timeSpentGood'
                                            }>
                                                {formatTime(answer.timeSpent)}
                                            </p>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TestReport;